import { useState } from "react";
import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Input,
  InputGroup,
  Spinner,
  Drawer,
  Portal,
  useBreakpointValue,
} from "@chakra-ui/react";
import { Checkbox } from "@chakra-ui/react/checkbox";
import { Dialog } from "@chakra-ui/react/dialog";
import { MdSearch as SearchIcon, MdClose as CloseIcon } from "react-icons/md";
import { useDebounce } from "@/hooks/useDebounce";
import { useRecipes } from '@/lib/recipeService';
import { useProgressiveList } from "@/hooks/useProgressiveList";
import { RecipeCollection } from "@/types/api";

interface AddRecipesModalProps {
  open: boolean;
  onClose: () => void;
  group: RecipeCollection | null;
  onAddRecipes: (recipeIds: string[]) => void;
  isAddingRecipes: boolean;
}

export default function AddRecipesModal({
  open,
  onClose,
  group,
  onAddRecipes,
  isAddingRecipes,
}: AddRecipesModalProps) {
  // Match the established BottomNavBar/CollectionsDialog pattern — no { ssr: false }
  const isMobile = useBreakpointValue({ base: true, lg: false });

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRecipeIds, setSelectedRecipeIds] = useState<string[]>([]);
  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  const { recipes: allRecipes, isLoading: isLoadingRecipes } = useRecipes({ search: debouncedSearchTerm });

  const { visibleItems: visibleRecipes, sentinelRef, containerRef: scrollContainerRef } = useProgressiveList(
    allRecipes,
    { pageSize: 30, resetKey: debouncedSearchTerm, useScrollContainer: true, rootMargin: '100px' }
  );

  const isRecipeInCollection = (recipeId: string) => {
    return group?.recipeIds?.includes(recipeId) || false;
  };

  const toggleSelectRecipe = (id: string) => {
    setSelectedRecipeIds(prev => prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]);
  };

  const handleAddSelected = () => {
    onAddRecipes(selectedRecipeIds);
    setSelectedRecipeIds([]);
  };

  const handleClose = () => {
    setSelectedRecipeIds([]);
    onClose();
  };

  const dragHandle = (
    <Box pt={3} pb={1} display="flex" justifyContent="center" data-testid="drag-handle">
      <Box w="36px" h="4px" borderRadius="full" bg="border.muted" />
    </Box>
  );

  const closeButton = (
    <Box
      as="button"
      aria-label="Close"
      onClick={handleClose}
      w="30px"
      h="30px"
      borderRadius="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
      color="text.muted"
      bg="bg.muted"
      border="none"
      cursor="pointer"
      outline="none"
      _hover={{ bg: 'border.muted', color: 'text.primary' }}
      _focusVisible={{ boxShadow: 'none', outline: 'none' }}
      transition="all 0.15s"
      flexShrink={0}
    >
      <CloseIcon size={15} />
    </Box>
  );

  const headerContent = (
    <Box display="flex" alignItems="center" justifyContent="space-between" w="100%">
      <Text fontFamily="heading" fontWeight="800" fontSize="lg" color="text.primary">
        Add recipes to {group?.title || 'collection'}
      </Text>
      {closeButton}
    </Box>
  );

  const searchContent = (
    <InputGroup startElement={<SearchIcon color="var(--chakra-colors-text-muted)" size={18} />}>
      <Input
        placeholder="Search recipes..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        fontFamily="body"
        fontSize="sm"
        bg="bg.canvas"
        borderColor="border.muted"
        borderWidth="1.5px"
        _focus={{ borderColor: "accent.secondary", boxShadow: "none", bg: "white" }}
        _placeholder={{ color: "text.muted", fontWeight: "400" }}
      />
    </InputGroup>
  );

  const recipeListContent = (
    <VStack
      ref={scrollContainerRef}
      gap={2}
      align="stretch"
      flex={isMobile ? 1 : undefined}
      minH={0}
      maxH={isMobile ? undefined : "400px"}
      overflowY="auto"
      bg="bg.canvas"
      border="1px solid"
      borderColor="border.muted"
      borderRadius="lg"
      p={2}
    >
      {isLoadingRecipes ? (
        <VStack py={8} gap={2}>
          <Spinner size="md" color="accent.secondary" />
          <Text fontFamily="body" fontSize="sm" color="text.muted">Loading recipes...</Text>
        </VStack>
      ) : (
        <>
          {visibleRecipes.map((recipe) => {
            const isInCollection = isRecipeInCollection(recipe.id);
            const selected = selectedRecipeIds.includes(recipe.id);
            return (
              <HStack
                key={recipe.id}
                px={3}
                py="10px"
                border="1px solid"
                borderColor={selected ? "accent.emphasis" : "border.muted"}
                borderRadius="md"
                justify="space-between"
                bg={selected ? "accent.subtle" : "bg.surface"}
                _hover={{ bg: isInCollection ? "bg.canvas" : selected ? "accent.subtle" : "bg.muted" }}
                opacity={isInCollection ? 0.55 : 1}
                cursor={isInCollection ? "not-allowed" : "pointer"}
                onClick={() => !isInCollection && toggleSelectRecipe(recipe.id)}
                transition="background 0.15s, border-color 0.15s"
              >
                <Text
                  fontFamily="body"
                  fontWeight={selected ? "500" : "300"}
                  fontSize="sm"
                  color="text.primary"
                  flex="1"
                  pr={2}
                >
                  {recipe.title}
                </Text>
                {isInCollection ? (
                  <Text fontFamily="body" fontSize="xs" fontWeight="500" color="accent.emphasis">
                    Added
                  </Text>
                ) : (
                  <Checkbox.Root
                    checked={selected}
                    onCheckedChange={() => toggleSelectRecipe(recipe.id)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control
                      borderColor={selected ? "accent.emphasis" : "border.muted"}
                      bg={selected ? "accent.emphasis" : undefined}
                      _hover={{ borderColor: "accent.emphasis" }}
                    >
                      <Checkbox.Indicator />
                    </Checkbox.Control>
                  </Checkbox.Root>
                )}
              </HStack>
            );
          })}

          {allRecipes.length === 0 && !isLoadingRecipes && (
            <Text
              fontFamily="body"
              fontWeight="300"
              fontSize="sm"
              color="text.muted"
              textAlign="center"
              py={4}
            >
              {debouncedSearchTerm ? "No recipes found" : "No recipes available"}
            </Text>
          )}
          <Box ref={sentinelRef} h="1px" />
        </>
      )}
    </VStack>
  );

  const footerContent = (
    <HStack justify="space-between" w="100%">
      <Text fontFamily="body" fontSize="sm" fontWeight="300" color="text.muted">
        {selectedRecipeIds.length} selected
      </Text>
      <HStack gap={2}>
        <Button
          size="sm"
          variant="ghost"
          fontFamily="body"
          fontWeight="500"
          color="text.muted"
          _hover={{ bg: "bg.muted" }}
          onClick={() => setSelectedRecipeIds([])}
          disabled={!selectedRecipeIds.length}
        >
          Clear
        </Button>
        <Button
          size="sm"
          bg="accent.emphasis"
          color="white"
          fontFamily="body"
          fontWeight="500"
          _hover={{ bg: "accent.emphasisHover" }}
          disabled={!selectedRecipeIds.length || isAddingRecipes}
          loading={isAddingRecipes}
          onClick={handleAddSelected}
        >
          Add selected ({selectedRecipeIds.length})
        </Button>
      </HStack>
    </HStack>
  );

  if (isMobile) {
    return (
      <Drawer.Root open={open} onOpenChange={(e) => { if (!e.open) handleClose(); }} placement="bottom">
        <Portal>
          <Drawer.Backdrop style={{ background: 'rgba(0,0,0,0.6)' }} />
          <Drawer.Positioner>
            <Drawer.Content
              bg="bg.surface"
              roundedTop="2xl"
              maxH="90vh"
              display="flex"
              flexDirection="column"
              pb="calc(env(safe-area-inset-bottom, 0px) + 16px)"
            >
              {dragHandle}
              <Drawer.Header borderBottomWidth="1px" borderColor="border.muted" bg="bg.surface" py={4} px={5}>
                {headerContent}
              </Drawer.Header>
              <Drawer.Body px={5} pt={5} pb={4} display="flex" flexDirection="column" gap={4} flex={1} overflow="hidden">
                {searchContent}
                {recipeListContent}
              </Drawer.Body>
              <Drawer.Footer borderTopWidth="1px" borderColor="border.muted" px={5} py={3}>
                {footerContent}
              </Drawer.Footer>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={(e) => { if (!e.open) handleClose(); }} size="lg">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content
          bg="bg.surface"
          border="1px solid"
          borderColor="border.muted"
          shadow="xl"
          borderRadius="xl"
          overflow="hidden"
          mx={{ base: 3, sm: 0 }}
        >
          <Dialog.Header borderBottomWidth="1px" borderColor="border.muted" bg="bg.surface" py={4} px={5}>
            {headerContent}
          </Dialog.Header>
          <Dialog.Body pb={4} px={{ base: 4, sm: 5 }} pt={5}>
            <VStack gap={4} align="stretch">
              {searchContent}
              {recipeListContent}
              <Box
                pt={3}
                borderTop="1px solid"
                borderColor="border.muted"
              >
                {footerContent}
              </Box>
            </VStack>
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
